/*! TM cue coordinator
 * T3 (2026-09 launch fixes). The cart drawer must show at most two
 * single-line promo messages (heading, items, <=2 cues, subtotal, checkout —
 * no tier tables in the drawer). Three independent banners can each want a
 * slot: tm-shipping-cue (universal), tbm-token-bundle / the MTG ladder
 * (only relevant when the cart has MTG single tokens), tm-gift-cue
 * (universal, £40 store-wide threshold).
 *
 * Rosie's picked rule (2026-09-19): show whichever two are most relevant to
 * THIS cart, not a fixed pair.
 *   - Shipping always gets a slot (real checkout-cost information, not just
 *     a promo, and applies to every cart).
 *   - The second slot goes to the ladder banner when the cart contains at
 *     least one MTG single token (reuses tbm-token-bundle's own
 *     lastCartHasMtg-style gate — see below); otherwise it goes to the gift
 *     cue.
 *   - This coordinator does not compute cart contents itself — the three
 *     banner scripts already fetch /cart.js independently and know their
 *     own eligibility. It only decides, once per cart-state change,
 *     which TWO of the three drawer wraps get `hidden` cleared in the
 *     drawer's fixed slots. Cart-page (/cart) is unaffected: all three can
 *     show there in full (that's where the full detail already lives per
 *     the existing 10 Jul "drawer slim mode" CSS on tbm-token-bundle).
 *
 * This script is deliberately tiny and DOM-only: it does not duplicate any
 * banner's own math. It listens for the same cart-mutation signals the
 * three banners already listen for, re-reads /cart.js once itself (cheap,
 * cached-friendly, same endpoint), decides eligibility, and toggles
 * visibility on the three fixed drawer-slot wrappers defined in
 * cart-drawer.liquid: [data-tm-shipping-cue-slot], [data-tbm-ladder-slot],
 * [data-tm-gift-cue-slot].
 */
(function () {
  'use strict';

  var MTG_GATE_SELECTOR_DATA_ID = 'tbm-bundle-data';

  function getMtgGateIds() {
    var el = document.getElementById(MTG_GATE_SELECTOR_DATA_ID);
    if (!el) return new Set();
    try {
      var cfg = JSON.parse(el.textContent);
      return new Set(cfg.mtg_gate_product_ids || []);
    } catch (e) {
      return new Set();
    }
  }

  function cartHasMtg(cart, mtgGateIds) {
    if (!mtgGateIds.size) return false;
    if (!cart || !Array.isArray(cart.items)) return false;
    for (var i = 0; i < cart.items.length; i++) {
      if (mtgGateIds.has(cart.items[i].product_id)) return true;
    }
    return false;
  }

  function setSlotVisible(drawer, selector, visible) {
    var slot = drawer.querySelector(selector);
    if (!slot) return;
    slot.hidden = !visible;
  }

  function applyToDrawer(drawer, showLadder) {
    setSlotVisible(drawer, '[data-tm-shipping-cue-slot]', true); // always on
    setSlotVisible(drawer, '[data-tbm-ladder-slot]', showLadder);
    setSlotVisible(drawer, '[data-tm-gift-cue-slot]', !showLadder);
  }

  function applyAll(showLadder) {
    document
      .querySelectorAll(
        'cart-drawer-component, cart-drawer, dialog[id*="cart-drawer"], .cart-drawer, [data-cart-drawer]'
      )
      .forEach(function (drawer) {
        applyToDrawer(drawer, showLadder);
      });
  }

  var fetchInFlight = null;
  function refresh() {
    if (fetchInFlight) return fetchInFlight;
    fetchInFlight = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        var showLadder = cartHasMtg(cart, getMtgGateIds());
        applyAll(showLadder);
      })
      .catch(function () {})
      .finally(function () {
        fetchInFlight = null;
      });
    return fetchInFlight;
  }

  ['cart:updated', 'cart:refresh', 'cart-update', 'shopify:cart:updated', 'cart:change'].forEach(
    function (ev) {
      document.addEventListener(ev, function () {
        setTimeout(refresh, 90); // run after the individual banners' own 60ms refresh
      });
    }
  );

  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function () {
      var url = '';
      try {
        url = typeof arguments[0] === 'string' ? arguments[0] : arguments[0].url || '';
      } catch (e) {}
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        Promise.resolve(p).then(function () {
          setTimeout(refresh, 90);
        });
      }
      return p;
    };
  }

  document.addEventListener('DOMContentLoaded', function () {
    refresh();
  });
  if (document.readyState !== 'loading') {
    refresh();
  }
  // Retry a few times to catch drawers that mount after this script runs
  // (same pattern the three banner scripts already use).
  [50, 200, 500, 1200, 3000].forEach(function (ms) {
    setTimeout(refresh, ms);
  });

  // Re-run when a drawer is (re)constructed, since fresh slot elements need
  // their initial hidden state set before the user notices a flash of both.
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
        refresh();
        return;
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
